import * as React from "react"
import * as Antd from "antd"

import styles from "./ChatBackOffice.module.scss"
import AppText from "@components/general/AppText/AppText.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import useStreamChatBackofficeData from "@hooks/useStreamChatBackofficeData.tsx"
import useBackofficeChatNotifications from "@hooks/useBackofficeChatNotifications.ts"

import { AppLanguages } from "@constants/AppLanguages.ts"
import { useTranslation } from "react-i18next"
import { Chat, Streami18n } from "stream-chat-react"
import { TranslationLanguages } from "stream-chat"

export interface IChatBackOfficeWrapperProps {
	className?: string
	children: React.ReactNode
}

const ChatBackOfficeWrapper: React.FC<IChatBackOfficeWrapperProps> = ({
	className,
	children,
}) => {
	const { i18n } = useTranslation()
	const { chatClient, status, initChat } = useStreamChatBackofficeData()

	const {
		supported: notificationSupported,
		permission: notificationPermission,
		canRequestPermission,
		isPermissionDenied,
		requestPermission,
	} = useBackofficeChatNotifications({
		chatClient,
		enabled: status === "ready",
	})

	const [isI18nReady, setIsI18nReady] = React.useState(false)
	const [i18nInstance] = React.useState(
		() =>
			new Streami18n({
				language: AppLanguages.DEFAULT as TranslationLanguages,
			})
	)

	React.useEffect(() => {
		let isCancelled = false

		i18nInstance
			.setLanguage(i18n.language as TranslationLanguages)
			.then(() => {
				if (!isCancelled) {
					setIsI18nReady(true)
				}
			})
			.catch((error) => {
				console.error("Error setting backoffice chat language:", error)

				if (!isCancelled) {
					setIsI18nReady(true)
				}
			})

		return () => {
			isCancelled = true
		}
	}, [i18n.language, i18nInstance])

	const content =
		status === "ready" && chatClient && isI18nReady ? (
			<Chat client={chatClient} i18nInstance={i18nInstance}>
				<div className={styles.chat_wrapper_content}>
					{notificationSupported &&
						notificationPermission !== "granted" && (
							<div
								className={`${styles.notification_prompt} ${isPermissionDenied ? styles.notification_prompt_denied : ""}`}
							>
								<div className={styles.notification_prompt_copy}>
									<AppText
										className={
											styles.notification_prompt_text
										}
										textKey={
											isPermissionDenied
												? "message_chat_backoffice_notifications_denied"
												: "message_chat_backoffice_notifications_prompt"
										}
									/>

									{isPermissionDenied && (
										<AppText
											className={
												styles.notification_prompt_help
											}
											textKey="message_chat_backoffice_notifications_denied_help"
										/>
									)}
								</div>

								{canRequestPermission && (
									<DsButton
										className={
											styles.notification_prompt_button
										}
										onClick={() => {
											void requestPermission()
										}}
										textKey="enable_notifications"
										type="secondary"
									/>
								)}
							</div>
						)}

					{children}
				</div>
			</Chat>
		) : status === "error" ? (
			<div className={styles.feedback}>
				<div className={styles.feedback_content}>
					<AppText
						className={styles.feedback_title}
						tag="h1"
						textKey="message_chat_load_failed_title"
					/>

					<AppText
						className={styles.feedback_description}
						textKey="message_chat_load_failed_description"
					/>

					<DsButton
						className={styles.feedback_button}
						onClick={() => {
							void initChat()
						}}
						textKey="try_again"
					/>
				</div>
			</div>
		) : (
			<Antd.Spin className={styles.spin} />
		)

	if (!className) return content

	return <div className={className}>{content}</div>
}

export default React.memo(ChatBackOfficeWrapper)
